import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import styles from './Legal.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormNewCheckbox from '../../../../components/formCheckbox/FormNewCheckbox';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface OperationalProps {}

const Legal: React.FC<OperationalProps> = ({}) => {
  const navigate = useNavigate();

  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext();
  const {
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = formMethods;
  const { legal } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, [legal?.compulsory]);

  //#region Handler

  const getQuesWithMarks = (impactTitle: 'legal') => {
    const selectedImpact = LegalData.impactQuestions.find(
      (impact) => impact.title === impactTitle
    );

    const getQuesWithMarksBySection = (sectionId: 'legal') => {
      return selectedImpact?.questions.map((ques, quesIdx) => {
        let selectedCheckbox = ques.checkbox.find((check) => {
          return check.marks == getValues(sectionId)[quesIdx];
        });

        let selectedAnswer = selectedCheckbox?.label;

        return {
          question: ques.question,
          marks: selectedCheckbox?.marks || 0,
          answer: selectedAnswer || '',
        };
      });
    };

    switch (impactTitle) {
      case 'legal':
        return getQuesWithMarksBySection('legal');
    }
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = LegalData.compulsoryQuestions.map((comp) => {
      return {
        answer: getValues('legal.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      };
    });

    const impactQuestions = LegalData.impactQuestions.flatMap(
      (imp) =>
        getQuesWithMarks(imp.title as any)?.map((q) => ({
          ...q,
          answerNotes: [],
          questionNotes: [],
        })) || []
    );

    const probabilityQuestions = LegalData.probabilityQuestions.map((comp) => {
      return {
        answer:
          comp.inputs.find(
            (inp) => inp.value == Number(getValues('legal.probability'))
          )?.label || '',
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
        marks: Number(getValues('legal.probability')),
      };
    });

    const finalOperationalData = new QuestionForm(
      LegalData.title,
      LegalData.description,
      compulsoryQuestions,
      impactQuestions,
      probabilityQuestions
    );

    setQuestion({ legal: finalOperationalData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      legal?.compulsory == '' ? undefined : legal?.compulsory == 'Yes'
    );

    navigate('/risk-transfer');
  };

  const generateRandomData = (data: any[]) => {
    const result: any = {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: Math.floor(Math.random() * 5) + 1,
    };

    data.forEach((section) => {
      section.questions.forEach((q: any, index: any) => {
        const randomIndex = Math.floor(Math.random() * q.checkbox.length);
        const option = q.checkbox[randomIndex];
        result[index] = option.marks; // store marks directly
      });
    });

    return result;
  };
  //#endregion

  const LegalData = {
    title: 'Legal Risk',
    description:
      'Legal risk refers to the potential for financial loss or harm to an organization due to legal or regulatory actions, including lawsuits, fines, or other penalties.',
    compulsoryQuestions: [
      {
        id: 'legal.compulsory',
        question: 'Is your Company exposed to legal risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'legal.probability',
        question: 'How frequent is the Company facing the risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        title: 'Legal risk assessment questionnaire',
        questions: [
          {
            id: 'legal.0',
            question: 'Review and update contracts regularly.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.1',
            question: 'Identify and protect intellectual property.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.2',
            question: 'Ensure compliance with regulatory requirements.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.3',
            question: 'Conduct due diligence on business partners and vendors.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.4',
            question:
              'Implement policies and procedures for data privacy and security.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.5',
            question: 'Train employees on legal compliance and risks.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.6',
            question:
              'Monitor and respond to legal developments and changes in laws and regulations.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.7',
            question: 'Maintain accurate and complete records.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.8',
            question:
              'Conduct risk assessments for potential legal liabilities.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
          {
            id: 'legal.9',
            question:
              'Have a crisis management plan in place for emergencies and legal disputes.',
            checkbox: [
              { label: 'Reviewed', marks: 2 },
              { label: 'Partially reviewed', marks: 1 },
              { label: 'Pending review', marks: 0 },
            ],
          },
        ],
      },
    ],
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="legal"
          title={LegalData.title}
          description={LegalData.description}
          compulsoryQuestions={LegalData.compulsoryQuestions}
          probabilityQuestions={LegalData.probabilityQuestions}
        />

        {legal?.compulsory && legal?.compulsory == 'Yes' && (
          <div>Legal Risk Assessment Questionnaire</div>
        )}

        {legal?.compulsory &&
          legal?.compulsory == 'Yes' &&
          LegalData.impactQuestions.map((impact, idx) => (
            <SectionContainer key={idx}>
              <Title>{impact.title}</Title>

              {impact.questions.map((ques, idx) => (
                <div key={idx}>
                  <div>
                    {idx + 1}. {ques.question}
                  </div>
                  {ques.checkbox.map((check, index) => (
                    <FormNewCheckbox
                      key={index}
                      id={ques.id}
                      label={check.label}
                      value={check.marks}
                      required
                    />
                  ))}
                </div>
              ))}
            </SectionContainer>
          ))}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              legal: {
                ...getValues().legal,
                ...generateRandomData(LegalData.impactQuestions),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Legal;
